<template>
  <CoreSelectionDrawer
    ref="selectionDrawerRef"
    has-search
    has-footer
    is-checked-variant
    :value="isVisibleModal"
    :title="t(modalTitle, options)"
    :placeholder="t(modalPlaceholder, options)"
    :items="places"
    :selected-item="selectedPlace"
    :error="isError"
    :loading="isLoading"
    :is-back="isBack"
    attach=".vue-the-doctors-filters .v-application"
    @click-item="onClickPlace"
    @input="closeModal"
    @back="closeModal"
  >
    <template #append-header>
      <v-btn
        v-if="!isPlaceDefault"
        color="primary"
        small
        text
        class="ml-auto"
        @click="clickResetBtn"
      >
        {{ t('Сбросить', options) }}
      </v-btn>
    </template>
    <template #footer>
      <slot
        name="footer"
        v-bind="{ selectedPlace }"
      >
        <div class="pa-2 elevation-8">
          <v-btn
            block
            depressed
            color="primary"
            :disabled="!filtersStore.hasUnsavedFilters"
            @click="$emit('place:selected', selectedPlace)"
          >
            {{ t('Применить', options) }}
          </v-btn>
        </div>
      </slot>
    </template>
  </CoreSelectionDrawer>
</template>

<script setup lang="ts">
import {
  computed, watch, ref, toRefs,
} from 'vue'
import I18NextVue from 'i18next'
import useCommonStore from 'www/vue_components/common/TheFilters/doctors/stores/useCommonStore'
import useFiltersStore from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore'
import CoreSelectionDrawer from 'src/core/CoreSelectionDrawer/CoreSelectionDrawer.vue'
import type { Filters } from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore.types'
import { DEFAULT_PLACE } from 'www/vue_components/common/TheFilters/doctors/components/PlaceFilters/constants'
import apiMetrosAndDistricts from 'www/vue_components/common/TheFilters/doctors/api/apiMetrosAndDistricts'
import mapPlace from 'www/vue_components/common/TheFilters/doctors/components/PlaceFilters/functions/mapPlace'

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const emit = defineEmits(['input', 'place:selected'])
const props = defineProps<{
  value: boolean
  isBack?: boolean
}>()

const { value: isVisibleModal } = toRefs(props)

const commonStore = useCommonStore()
const filtersStore = useFiltersStore()

const places = ref<NonNullable<Filters['place']>[]>([])
const selectedPlace = computed(() => filtersStore.unsavedFilters.place)
const isPlaceDefault = computed(() => !selectedPlace.value)
const isError = ref(false)
const isLoading = ref(false)
const modalTitle = computed(() => (commonStore.doctorList.hasMetro ? 'Метро или район' : 'Район'))
const modalPlaceholder = computed(() => (commonStore.doctorList.hasMetro ? 'Название станции или района' : 'Название района'))
const selectionDrawerRef = ref<InstanceType<typeof CoreSelectionDrawer>>()

const loadPlaces = async () => {
  isError.value = false
  isLoading.value = true

  try {
    const { data } = await apiMetrosAndDistricts.get({
      town_id: commonStore.town.id,
    })

    places.value = [
      { ...DEFAULT_PLACE, name: t(DEFAULT_PLACE.name, options.value) },
      ...data.metro.map(x => mapPlace(x, true)),
      ...data.district.map(x => mapPlace(x, false)),
    ]
  } catch {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}

watch(isVisibleModal, () => {
  if (isVisibleModal.value && !places.value.length) {
    loadPlaces()
  }
})

const restartModal = () => {
  selectionDrawerRef.value?.resetSearch()
  selectionDrawerRef.value?.scrollToActiveItem()
}
const closeModal = () => {
  emit('input', false)
  restartModal()
}

const clickResetBtn = () => {
  filtersStore.updateUnsavedFilters({ place: undefined })
  restartModal()
}

const isDefaultPlace = (place: Filters['place']) => !place || place.translit === DEFAULT_PLACE.translit
const getValidPlace = (place: Filters['place']) => (!isDefaultPlace(place) ? place : undefined)
const onClickPlace = (place: Filters['place']) => {
  filtersStore.updateUnsavedFilters({ place: getValidPlace(place) })
}
</script>
